'use client'

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { VENDOR_ID_PATTERNS } from '@sifly/shared-utils'

// The dealer's GoHighLevel (LeadConnector) chat bubble. Visitor messages land
// in the dealer's GHL Conversations inbox; nothing passes through SiFly.
//
// Not consent-gated: like the FareHarbor widget it is a feature the visitor
// uses, not a measurement tag, and the generated privacy disclosure lists it
// under "Always on" (privacyDisclosure.ts).
//
// Script loading mirrors AnalyticsScripts.tsx: DOM-injected (the security lint
// blocks inline-HTML scripts), load-once guarded. It waits for the page's load
// event and then an idle slot, so the bubble never competes with the LCP.
//
// Kept off /checkout like the other overlays (HiddenOnCheckout): a hard load
// there never injects the script, and a bubble already loaded by client nav is
// hidden by a CSS rule on html[data-sf-hide-chat] (globals.css). The pathname
// is read here, live, because the root layout does not re-render on client nav.

declare global {
  interface Window {
    __siflyGhlChatLoaded?: boolean
  }
}

function inject(widgetId: string) {
  if (window.__siflyGhlChatLoaded) return
  window.__siflyGhlChatLoaded = true
  const s = document.createElement('script')
  s.async = true
  s.src = 'https://widgets.leadconnectorhq.com/loader.js'
  s.setAttribute('data-resources-url', 'https://widgets.leadconnectorhq.com/chat-widget/loader.js')
  s.setAttribute('data-widget-id', widgetId)
  document.body.appendChild(s)
}

export function GhlChatWidget({ widgetId }: { widgetId: string | null }) {
  const onCheckout = usePathname() === '/checkout'

  useEffect(() => {
    document.documentElement.toggleAttribute('data-sf-hide-chat', onCheckout)
  }, [onCheckout])

  useEffect(() => {
    // Re-validated at the render boundary: the value lands in a script attribute.
    if (onCheckout || !widgetId || !VENDOR_ID_PATTERNS.ghlChat.test(widgetId)) return
    let cancelled = false
    const start = () => {
      if (cancelled) return
      if ('requestIdleCallback' in window) window.requestIdleCallback(() => { if (!cancelled) inject(widgetId) }, { timeout: 3000 })
      else setTimeout(() => { if (!cancelled) inject(widgetId) }, 1500)
    }
    if (document.readyState === 'complete') start()
    else window.addEventListener('load', start, { once: true })
    return () => {
      cancelled = true
      window.removeEventListener('load', start)
    }
  }, [widgetId, onCheckout])

  return null
}
